// ============================================================================
// PATIENT DOCUMENTS BLOCK — Documentos adjuntos de todos los presupuestos
// Agrupados por tipo de documento. Click para visualizar.
// ============================================================================
import { useState, useEffect, useCallback } from 'react';
import { Paperclip, FileText, ExternalLink } from 'lucide-react';
import { budgetDocumentsApi, type BudgetDocument } from '../../api/budget-documents';
import { CollapsibleSection } from '../ui/CollapsibleSection';

interface PatientDocumentsBlockProps {
  patientId: string;
  userId?: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function PatientDocumentsBlock({ patientId, userId }: PatientDocumentsBlockProps) {
  const [documents, setDocuments] = useState<BudgetDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const load = useCallback(async () => {
    if (hasLoaded) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await budgetDocumentsApi.getByPatient(patientId);
      setDocuments(result);
      setHasLoaded(true);
    } catch (err: any) {
      setError(err?.message || 'Error al cargar documentos adjuntos');
    } finally {
      setIsLoading(false);
    }
  }, [patientId, hasLoaded]);

  useEffect(() => { load(); }, [load]);

  const handleView = (doc: BudgetDocument) => {
    const token = localStorage.getItem('imi_access_token') || '';
    const url = budgetDocumentsApi.getViewUrl(doc.budgetId, doc.id);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) throw new Error('No se pudo abrir el documento');
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      })
      .catch((err) => setError(err?.message || 'No se pudo abrir el documento'));
  };

  // Agrupar por tipo
  const grouped: Record<string, BudgetDocument[]> = {};
  for (const doc of documents) {
    const key = doc.documentTypeName || 'Sin tipo';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(doc);
  }

  // Ordenar dentro de cada grupo por fecha desc
  for (const key of Object.keys(grouped)) {
    grouped[key].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  const groupNames = Object.keys(grouped).sort((a, b) => a.localeCompare(b, 'es'));

  return (
    <CollapsibleSection
      id="documents"
      title="Documentos adjuntos"
      icon={<Paperclip className="h-5 w-5 text-gray-400" />}
      badge={
        <span className="text-sm font-normal text-gray-500">
          {documents.length} {documents.length === 1 ? 'documento' : 'documentos'}
        </span>
      }
      defaultOpen={false}
      userId={userId}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm mb-3">
          {error}
        </div>
      )}
      {isLoading && !hasLoaded ? (
        <p className="text-sm text-gray-400">Cargando...</p>
      ) : documents.length === 0 ? (
        <p className="text-sm text-gray-500 italic py-4 text-center">
          No hay documentos adjuntos para este paciente.
        </p>
      ) : (
        <div className="space-y-5">
          {groupNames.map((groupName) => (
            <div key={groupName}>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                {groupName}
                <span className="text-gray-400 font-normal">({grouped[groupName].length})</span>
              </h3>
              <div className="space-y-2">
                {grouped[groupName].map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    <FileText className="h-5 w-5 text-red-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => handleView(doc)}
                        className="text-sm font-medium text-gray-900 hover:text-[#008DD0] text-left truncate block w-full"
                      >
                        {doc.customTitle || doc.fileName}
                      </button>
                      <p className="text-xs text-gray-500 truncate">
                        <span className="font-mono">{doc.budgetNumber}</span>
                        {' · '}{formatDate(doc.createdAt)}
                        {' · '}{formatBytes(doc.fileSize)}
                        {doc.uploadedByName && <span> · {doc.uploadedByName}</span>}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleView(doc)}
                      className="p-2 text-gray-400 hover:text-[#008DD0] transition-colors"
                      title="Ver documento"
                      aria-label="Ver documento"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}

export default PatientDocumentsBlock;
